(function () {
  'use strict';

  /* Expose the actual sticky header height as --cg-header-h on <html>
     so .cg-product can size its top padding to clear it cleanly. The
     theme's header changes height across breakpoints (and again when
     the menu wraps to two rows), so a fixed CSS value is brittle. */
  function findHeader() {
    return (
      document.querySelector('.shopify-section-group-header-group')
      || document.querySelector('.shopify-section.section-header')
      || document.querySelector('[id*="header-group"]')
      || document.querySelector('[class*="section-header"]')
      || document.querySelector('.xo-header')
      || document.querySelector('header[role="banner"]')
      || document.querySelector('header')
    );
  }

  function syncHeaderHeight() {
    const header = findHeader();
    if (!header) return;
    const h = Math.ceil(header.getBoundingClientRect().height);
    if (h > 0) {
      document.documentElement.style.setProperty('--cg-header-h', h + 'px');
    }
  }

  syncHeaderHeight();
  document.addEventListener('DOMContentLoaded', syncHeaderHeight);
  window.addEventListener('load', syncHeaderHeight);
  window.addEventListener('resize', syncHeaderHeight);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(syncHeaderHeight);
  }
  if ('ResizeObserver' in window) {
    const headerEl = findHeader();
    if (headerEl) new ResizeObserver(syncHeaderHeight).observe(headerEl);
  }
  // Re-measure on Shopify theme editor section reloads (header changes).
  document.addEventListener('shopify:section:load', syncHeaderHeight);
  document.addEventListener('shopify:section:unload', syncHeaderHeight);

  const SECTION_SELECTOR = '.cg-product';
  const READMORE_SELECTOR = '[data-cg-readmore]';
  const TOGGLE_SELECTOR = '[data-cg-readmore-toggle]';
  const CLAMP_SELECTOR = '.cg-accordion__readmore-clamp';
  const EXPANDED_CLASS = 'is-expanded';
  const MORE_LABEL_SELECTOR = '[data-cg-readmore-label-more]';
  const LESS_LABEL_SELECTOR = '[data-cg-readmore-label-less]';

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getActiveThreshold(section) {
    const desktop = parseInt(section.dataset.cgRmDesktop || '240', 10);
    const mobile = parseInt(section.dataset.cgRmMobile || '160', 10);
    return window.matchMedia('(min-width: 768px)').matches ? desktop : mobile;
  }

  function refreshReadmore(wrap, section) {
    const clamp = wrap.querySelector(CLAMP_SELECTOR);
    const toggle = wrap.querySelector(TOGGLE_SELECTOR);
    if (!clamp || !toggle) return;

    // Skip when not laid out (e.g. inside a closed accordion). We'll measure
    // again when the accordion opens via the MutationObserver below.
    if (clamp.offsetParent === null && !wrap.classList.contains(EXPANDED_CLASS)) return;

    const threshold = getActiveThreshold(section);
    const isOverflowing = clamp.scrollHeight > threshold + 4;

    if (isOverflowing) {
      toggle.hidden = false;
    } else {
      toggle.hidden = true;
      // Content fits — drop any stale expanded state so the clamp resumes.
      wrap.classList.remove(EXPANDED_CLASS);
      const moreLabel = toggle.querySelector(MORE_LABEL_SELECTOR);
      const lessLabel = toggle.querySelector(LESS_LABEL_SELECTOR);
      if (moreLabel) moreLabel.hidden = false;
      if (lessLabel) lessLabel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  function bindToggle(wrap) {
    const toggle = wrap.querySelector(TOGGLE_SELECTOR);
    if (!toggle || toggle.dataset.cgBound === 'true') return;
    toggle.dataset.cgBound = 'true';

    const moreLabel = toggle.querySelector(MORE_LABEL_SELECTOR);
    const lessLabel = toggle.querySelector(LESS_LABEL_SELECTOR);

    toggle.addEventListener('click', () => {
      const expanded = wrap.classList.toggle(EXPANDED_CLASS);
      if (moreLabel) moreLabel.hidden = expanded;
      if (lessLabel) lessLabel.hidden = !expanded;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function initSection(section) {
    if (section.dataset.cgInited === 'true') return;
    section.dataset.cgInited = 'true';

    const wraps = section.querySelectorAll(READMORE_SELECTOR);
    wraps.forEach((wrap) => bindToggle(wrap));

    const refreshAll = () => {
      wraps.forEach((wrap) => refreshReadmore(wrap, section));
    };

    requestAnimationFrame(refreshAll);

    // xo-collapse toggles its open state via an attribute. Re-measure when
    // any collapse opens so previously hidden content can be evaluated.
    if ('MutationObserver' in window) {
      const collapses = section.querySelectorAll('xo-collapse');
      if (collapses.length) {
        const obs = new MutationObserver(() => requestAnimationFrame(refreshAll));
        collapses.forEach((c) => {
          obs.observe(c, { attributes: true, attributeFilter: ['xo-active'] });
        });
      }
    }

    window.addEventListener('resize', debounce(refreshAll, 250));

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => requestAnimationFrame(refreshAll));
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is reloaded
  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (!target) return;
    if (target.matches && target.matches(SECTION_SELECTOR)) {
      initSection(target);
    } else if (target.querySelector) {
      const found = target.querySelector(SECTION_SELECTOR);
      if (found) initSection(found);
    }
  });
})();
